"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { ArrowRight } from "@/components/icons";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";

export type CtaProduct = "residential" | "mobile" | "chooser";
export type CtaPlacement = "after_summary" | "inline" | "end_of_post";

interface CtaLink {
  label: string;
  href: string;
}

interface CtaContent {
  heading: string;
  body: string;
  primary: CtaLink;
  secondary: CtaLink;
}

const CTA_CONTENT: Record<CtaProduct, CtaContent> = {
  residential: {
    heading: "Need proxies for localized monitoring or public-data collection?",
    body: "Use rotating or sticky residential IPs with country, city, and ISP targeting across 195+ countries. Plans start at $1/GB.",
    primary: { label: "Explore Residential Proxies", href: ROUTES.productResidentialProxies },
    secondary: { label: "View Residential Pricing", href: ROUTES.pricingResidentialProxies },
  },
  mobile: {
    heading: "Need a premium mobile IP?",
    body: "Use managed 4G/5G mobile proxies with city and ASN/ISP targeting, rotating or sticky sessions, and HTTP(S)/SOCKS5 support. Self-serve plans start at $50 for 25 GB ($2/GB).",
    primary: { label: "Explore Mobile Proxies", href: ROUTES.productMobileProxies },
    secondary: { label: "View Mobile Pricing", href: ROUTES.pricingMobileProxies },
  },
  chooser: {
    heading: "Not sure which proxy network fits?",
    body: "Choose residential proxies for broad global scraping, SEO monitoring, price monitoring, market research, and AI data collection. Choose mobile proxies when the workflow specifically requires a mobile network identity.",
    primary: { label: "Compare Proxy Plans", href: ROUTES.pricing },
    secondary: { label: "Explore Residential Proxies", href: ROUTES.productResidentialProxies },
  },
};

const COMPACT_LINK_LABEL: Record<CtaProduct, string> = {
  residential: "Compare residential proxy plans",
  mobile: "Compare mobile proxy plans",
  chooser: "Compare residential and mobile proxy options",
};

interface Props {
  product: CtaProduct;
  placement: CtaPlacement;
  postSlug: string;
  className?: string;
}

/** Article-specific product CTA. `after_summary` is a one-line text link that
 *  catches readers who got their answer early; `inline` and `end_of_post` are the
 *  full card with the product, starting price, and a pricing link. `inline` sits
 *  where an editor places a `[[cta]]` marker, so it is tighter and uses an h3. */
export default function ProductCta({ product, placement, postSlug, className }: Props) {
  const posthog = usePostHog();
  const content = CTA_CONTENT[product];

  function track(target: string) {
    posthog.capture("blog_product_cta_clicked", {
      post_slug: postSlug,
      product,
      placement,
      target,
    });
  }

  if (placement === "after_summary") {
    const href = product === "chooser" ? ROUTES.pricing : content.primary.href;
    return (
      <p className={cn("text-muted-foreground text-sm", className)}>
        Need proxy infrastructure for this workflow?{" "}
        <Link
          href={href}
          onClick={() => track(href)}
          className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
        >
          {COMPACT_LINK_LABEL[product]}
        </Link>
        .
      </p>
    );
  }

  const isInline = placement === "inline";
  const Heading = isInline ? "h3" : "h2";

  return (
    <aside
      aria-label="Recommended proxy product"
      className={cn(
        "border-border bg-card rounded-2xl border",
        isInline ? "my-8 p-6 sm:p-8" : "mt-15 p-8 sm:p-10",
        className
      )}
    >
      <Heading
        className={cn(
          "text-foreground mb-3 max-w-xl leading-tight font-bold",
          isInline ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"
        )}
      >
        {content.heading}
      </Heading>
      <p className="text-muted-foreground mb-7 max-w-2xl text-sm leading-relaxed sm:text-base">
        {content.body}
      </p>
      <div className="flex flex-wrap gap-3">
        <Link
          href={content.primary.href}
          onClick={() => track(content.primary.href)}
          className="bg-foreground text-background inline-flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
        >
          {content.primary.label}
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
        <Link
          href={content.secondary.href}
          onClick={() => track(content.secondary.href)}
          className="border-border text-foreground hover:border-foreground/40 hover:bg-foreground/5 inline-flex items-center rounded-lg border px-5 py-2.5 text-sm font-semibold transition-colors"
        >
          {content.secondary.label}
        </Link>
      </div>
    </aside>
  );
}
